import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Clock, Globe } from "lucide-react";
import { useState, useMemo } from "react";
import {
  formatAvailabilityText,
  isCurrentlyOnline,
  ScheduleData,
} from "@/lib/utils";
import { DEFAULT_TIMEZONE } from "@/lib/constants";

const simulationTimezones = [
  DEFAULT_TIMEZONE,
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Adak",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney",
];

interface ScheduleSimulatorProps {
  scheduleData: ScheduleData;
}

export function ScheduleSimulator({ scheduleData }: ScheduleSimulatorProps) {
  const [selectedTimezone, setSelectedTimezone] = useState(
    scheduleData.timezone,
  );

  const availabilityText = useMemo(() => {
    return formatAvailabilityText(scheduleData, selectedTimezone);
  }, [scheduleData.schedule, scheduleData.timezone, selectedTimezone]);

  const isOnlineNow = useMemo(() => {
    return isCurrentlyOnline(scheduleData, selectedTimezone);
  }, [scheduleData.schedule, scheduleData.timezone, selectedTimezone]);

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Schedule Simulator
        </CardTitle>
        <CardDescription>
          See how your availability appears to teens in different timezones
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium">Viewing timezone:</span>
          <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {simulationTimezones.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="mb-1 text-sm text-gray-600">What teens see:</p>
              <p className="text-lg font-medium text-gray-900">
                {availabilityText}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Viewing in: {selectedTimezone} | Mentor timezone:{" "}
                {scheduleData.timezone}
              </p>
            </div>
            <Badge variant={isOnlineNow ? "default" : "secondary"}>
              {isOnlineNow ? "Online" : "Offline"}
            </Badge>
          </div>
        </div>

        <p className="text-xs text-gray-500">
          This preview shows how your schedule appears to teens in the selected
          timezone. The status updates in real-time based on your configured
          active hours.
        </p>
      </CardContent>
    </Card>
  );
}
